import {
  Box,
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { ReactComponent as HederaLogo } from 'assets/hedera-hbar-logo.svg';
import { ReactComponent as HederaLogoBlack } from 'assets/hedera-logo.svg';
import { ReactComponent as ArrowDownBlack } from 'assets/arrow-down-black-icon.svg';
import { useCallback, useContext, useRef, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { GlobalContext } from '../../providers/Global';
import { getNetworkColor, handleNetworkColor } from '../../utils/color';
import { ModalType, SupportedNetworks } from 'utils/constants';
import { useNetworkSwitchWarning } from 'hooks/useNetworkSwitchWarning';
import { styles } from './styles';
import { customNetworksEnabled } from 'utils/customNetworks';

const NetworksWidget = () => {
  const {
    connection: { accountId, disconnectWallet },
    connectedNetwork,
    updateConnectorNetwork,
    openModal,
  } = useContext(GlobalContext);

  const flexRef = useRef(null);
  const [showCustomWarning, setShowCustomWarning] = useState(false);

  const isSuppressed = () => {
    try { return localStorage.getItem('suppressNetworkSwitchWarning') === 'true'; } catch { return false; }
  };

  const handleNetworkName = useCallback((): string => {
    if (!connectedNetwork) return 'Network';
    if (connectedNetwork.startsWith('custom-')) {
      return 'Custom';
    }
    return connectedNetwork;
  }, [connectedNetwork]);

  const { onOpen, onClose, isOpen } = useDisclosure();

  const { handleSelection: selectNetwork, WarningModal } = useNetworkSwitchWarning({
    isAuthenticated: !!accountId,
    updateConnectorNetwork,
  });

  const handleSelection = useCallback(
    (network: SupportedNetworks) => {
      onClose();
      selectNetwork(network);
    },
    [onClose, selectNetwork],
  );

  // This overrides some really odd behaviour
  // of the Popover element handling itself open/closed state
  useOutsideClick({
    ref: flexRef,
    handler: onClose,
  });

  return (
    <>
      {WarningModal}
      <Modal isOpen={showCustomWarning} onClose={() => setShowCustomWarning(false)} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Switch Network?</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600" mb={4}>
              You are currently connected to {connectedNetwork}. Switching to a custom network will disconnect your current session.
            </Text>
            <Flex gap={3}>
              <Button flex={1} variant="outline" borderRadius={6} onClick={() => setShowCustomWarning(false)}>
                Cancel
              </Button>
              <Button flex={1} variant="primary" borderRadius={6} onClick={() => {
                setShowCustomWarning(false);
                disconnectWallet();
                openModal(ModalType.PrivateNetworksDrawer);
              }}>
                Switch
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Flex ml={4} w={140} alignItems="center">
        <Popover isOpen={isOpen}>
          <PopoverTrigger>
            <Flex
              ref={flexRef}
              onClick={isOpen ? onClose : onOpen}
              gap={1}
              sx={{ cursor: 'pointer', alignItems: 'center', maxHeight: '32px' }}
            >
              {connectedNetwork?.startsWith('custom-')
                ? <HederaLogoBlack />
                : <HederaLogo style={{ marginTop: '6px' }} />
              }
              <Text
                mt="2px"
                size="sm"
                mr="auto"
                textTransform={'capitalize'}
                fontWeight={600}
                color={handleNetworkColor(connectedNetwork)}
              >
                {handleNetworkName()}
              </Text>
              <ArrowDownBlack style={styles.rotatingArrow(isOpen)} />
            </Flex>
          </PopoverTrigger>
          <PopoverContent
            mt={5}
            p={1}
            mr={2.5}
            w={130}
            borderRadius={16}
            _focusVisible={{ outline: 'none ' }}
          >
            <PopoverBody p={0}>
              <Flex gap={2} flexDirection="column">
                {Object.entries(SupportedNetworks)
                  .filter(
                    ([_, n]) =>
                      n.toLowerCase() !== connectedNetwork && n !== SupportedNetworks.Previewnet,
                  )
                  .map(([key, network]) => (
                    <Flex
                      key={key}
                      sx={styles.popoverItem}
                      onClick={() => handleSelection(network)}
                    >
                      <HederaLogo style={{ marginTop: '5px' }} />
                      <Text fontWeight={600} size="sm" color={getNetworkColor(network)}>
                        {network}
                      </Text>
                    </Flex>
                  ))}
                {customNetworksEnabled() && (
                  <>
                    <Divider my={1} />
                    <Flex
                      sx={styles.popoverItem}
                      onClick={() => {
                        onClose();
                        if (accountId && connectedNetwork && !connectedNetwork.startsWith('custom-') && !isSuppressed()) {
                          setShowCustomWarning(true);
                        } else {
                          openModal(ModalType.PrivateNetworksDrawer);
                        }
                      }}
                    >
                      <Box w="32px" h="32px" display="flex" alignItems="center" justifyContent="center">
                        <HederaLogoBlack style={{ width: 24, height: 24 }} />
                      </Box>
                      <Text fontWeight={600} size="sm" color="gray.700">
                        Custom
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>
    </>
  );
};

export default NetworksWidget;
